import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Loader2, Package, Mail, Truck, AlertCircle } from 'lucide-react';
import { checkoutApi } from '../lib/api';
import { trackPurchase } from '../lib/analytics';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId || polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2500;

    const poll = async () => {
      try {
        const res = await checkoutApi.getStatus(sessionId);
        const data = res.data;
        setPaymentData(data);
        setOrder(data.order);

        if (data.payment_status === 'paid') {
          setStatus('success');
          trackPurchase(data.order, data.order?.id || sessionId);
          // Clear cart from localStorage
          localStorage.removeItem('ninjathlete_cart_id');
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, interval);
        } else {
          setStatus('timeout');
        }
      } catch (err) {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, interval);
        } else {
          setStatus('error');
        }
      }
    };

    poll();
  }, [sessionId]);

  if (!sessionId) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center" data-testid="checkout-success-no-session">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-white text-3xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>No Session Found</h1>
          <p className="text-zinc-400 mb-8">It looks like you arrived here without completing a checkout.</p>
          <Link to="/shop" className="btn-ninja inline-flex items-center gap-2">
            Go to Shop <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    );
  }

  if (status === 'loading') {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center" data-testid="checkout-success-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#E63946] mx-auto mb-4 animate-spin" />
          <h2 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Processing Payment</h2>
          <p className="text-zinc-400">Please wait while we confirm your payment...</p>
        </div>
      </main>
    );
  }

  if (status === 'expired' || status === 'error' || status === 'timeout') {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center" data-testid="checkout-success-error">
        <div className="text-center max-w-md mx-auto">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-white text-3xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            {status === 'expired' ? 'Session Expired' : 'Payment Verification Pending'}
          </h1>
          <p className="text-zinc-400 mb-8">
            {status === 'expired'
              ? 'Your payment session has expired. Please try again.'
              : 'We\'re still verifying your payment. You\'ll receive an email confirmation once it\'s processed.'}
          </p>
          <Link to="/shop" className="btn-ninja inline-flex items-center gap-2">
            Return to Shop <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen" data-testid="checkout-success-page">
      <div className="container-ninja section-spacing max-w-2xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-white text-4xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Order Confirmed
          </h1>
          <p className="text-zinc-400 text-lg">
            Thank you for your purchase{order?.customer_name ? `, ${order.customer_name.split(' ')[0]}` : ''}!
          </p>
        </div>

        {/* Order Details */}
        {order && (
          <div className="bg-zinc-900/30 border border-zinc-800 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Order Details
              </h3>
              <span className="text-zinc-500 text-sm">#{order.id?.slice(0, 8)}</span>
            </div>

            {/* Items */}
            <div className="space-y-3 mb-6">
              {order.items?.map((item, i) => (
                <div key={item.name} className="flex justify-between text-sm">
                  <span className="text-zinc-300">{item.name} x {item.quantity}</span>
                  <span className="text-zinc-300">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-zinc-800 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Subtotal</span>
                <span className="text-zinc-300">${order.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Shipping ({order.shipping_method})</span>
                <span className={order.shipping_cost === 0 ? 'text-green-400 text-sm' : 'text-zinc-300 text-sm'}>
                  {order.shipping_cost === 0 ? 'FREE' : `$${order.shipping_cost?.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-zinc-800">
                <span className="text-white">Total</span>
                <span className="text-white">${order.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          <div className="bg-zinc-900/30 border border-zinc-800 p-4 text-center">
            <Mail className="w-6 h-6 text-[#E63946] mx-auto mb-2" />
            <p className="text-white text-sm font-medium">Confirmation Email</p>
            <p className="text-zinc-500 text-xs mt-1">Sent to {order?.customer_email}</p>
          </div>
          <div className="bg-zinc-900/30 border border-zinc-800 p-4 text-center">
            <Package className="w-6 h-6 text-[#E63946] mx-auto mb-2" />
            <p className="text-white text-sm font-medium">Order Processing</p>
            <p className="text-zinc-500 text-xs mt-1">We'll prepare your order</p>
          </div>
          <div className="bg-zinc-900/30 border border-zinc-800 p-4 text-center">
            <Truck className="w-6 h-6 text-[#E63946] mx-auto mb-2" />
            <p className="text-white text-sm font-medium">Shipping Updates</p>
            <p className="text-zinc-500 text-xs mt-1">Tracking info via email</p>
          </div>
        </div>

        {/* Actions */}
        <div className="text-center">
          <Link to="/" className="btn-ninja inline-flex items-center gap-2">
            Continue Shopping <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </main>
  );
}
